import React from 'react';
import {useTranslation} from 'react-i18next';
import AdminSidebarSection from './AdminSidebarSection';
import {useAuth} from '../../../common/contexts/AuthContext';
import {UserRole} from '../../users/types/User';

interface SidebarLinkData {
  icon: string;
  text: string;
  link: string;
  allowedRoles: UserRole[];
}

const AdminSidebar: React.FC = () => {
  const {user} = useAuth();
  const {t} = useTranslation();

  const locationsLinks: SidebarLinkData[] = [
    {
      icon: 'fa-earth-europe',
      text: t('admin.common.sidebar.organizationalUnits'),
      link: '/organizational-units',
      allowedRoles: [UserRole.REDACTOR]
    },
    {
      icon: 'fa-place-of-worship',
      text: t('admin.common.sidebar.places'),
      link: '/places',
      allowedRoles: [UserRole.REDACTOR]
    },
    {
      icon: 'fa-palette',
      text: t('admin.common.sidebar.artisticObjects'),
      link: '/artistic-objects',
      allowedRoles: [UserRole.REDACTOR]
    },
    {icon: 'fa-users', text: t('admin.common.sidebar.people'), link: '/people', allowedRoles: [UserRole.REDACTOR]},
    {
      icon: 'fa-warehouse',
      text: t('admin.common.sidebar.workshops'),
      link: '/workshops',
      allowedRoles: [UserRole.REDACTOR]
    },
  ];

  const publicationsLinks: SidebarLinkData[] = [
    {
      icon: 'fa-book',
      text: t('admin.common.sidebar.publications'),
      link: '/publications',
      allowedRoles: [UserRole.REDACTOR]
    },
    {
      icon: 'fa-archive',
      text: t('admin.common.sidebar.archives'),
      link: '/archives',
      allowedRoles: [UserRole.REDACTOR]
    },
    {
      icon: 'fa-list',
      text: t('admin.common.sidebar.bibliographyItems'),
      link: '/bibliography-items',
      allowedRoles: [UserRole.REDACTOR]
    },
    {
      icon: 'fa-folder-open',
      text: t('admin.common.sidebar.collections'),
      link: '/collections',
      allowedRoles: [UserRole.USER, UserRole.REDACTOR]
    },
    {icon: 'fa-th-large', text: t('admin.common.sidebar.tiles'), link: '/tiles', allowedRoles: [UserRole.REDACTOR]},
    {icon: 'fa-file-alt', text: t('admin.common.sidebar.pages'), link: '/pages', allowedRoles: [UserRole.REDACTOR]},
  ];

  const dictionariesLinks: SidebarLinkData[] = [
    {icon: 'fa-sitemap', text: t('admin.common.sidebar.placeSections'), link: '/place-sections', allowedRoles: []},
    {
      icon: 'fa-sitemap',
      text: t('admin.common.sidebar.artisticObjectSections'),
      link: '/artistic-object-sections',
      allowedRoles: []
    },
  ];

  const usersLinks: SidebarLinkData[] = [
    {icon: 'fa-users', text: t('admin.common.sidebar.users'), link: '/users', allowedRoles: []},
  ];

  const filterLinksByRole = (links: SidebarLinkData[]): SidebarLinkData[] => {
    if (!user) return [];
    // ADMIN role is implicitly allowed for all links
    return links.filter(link => user.role === UserRole.ADMIN || link.allowedRoles.includes(user.role));
  };

  const filteredLocationsLinks = filterLinksByRole(locationsLinks);
  const filteredPublicationsLinks = filterLinksByRole(publicationsLinks);
  const filteredDictionariesLinks = filterLinksByRole(dictionariesLinks);
  const filteredUsersLinks = filterLinksByRole(usersLinks);

  return (
    <aside id="sidebar" className="w-64 bg-white shadow-lg">
      <div className="p-6">
        <h2 className="text-lg font-cinzel font-bold text-gray-900 mb-6">{t('admin.common.sidebar.administration')}</h2>

        {filteredLocationsLinks.length > 0 && (
          <AdminSidebarSection
            id="locations-section"
            title={t('admin.common.sidebar.locations')}
            links={filteredLocationsLinks}
          />
        )}

        {filteredPublicationsLinks.length > 0 && (
          <AdminSidebarSection
            id="publications-section"
            title={t('admin.common.sidebar.publications')}
            links={filteredPublicationsLinks}
          />
        )}

        {filteredDictionariesLinks.length > 0 && (
          <AdminSidebarSection
            id="dictionaries-section"
            title={t('admin.common.sidebar.dictionaries')}
            links={filteredDictionariesLinks}
          />
        )}

        {filteredUsersLinks.length > 0 && (
          <AdminSidebarSection
            id="users-section"
            title={t('admin.common.sidebar.users')}
            links={filteredUsersLinks}
          />
        )}
      </div>
    </aside>
  );
};

export default AdminSidebar;
